import { useMemo, useState } from 'react';
import { Plus, Search } from 'lucide-react';
import { AgentCard } from '@/components/agent-card/AgentCard';
import { NewColleagueDialog } from '@/components/colleague-dialog/NewColleagueDialog';
import { CardGridSkeleton } from '@/components/feedback/Skeletons';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAgents } from '@/api/queries';

export function AgentsPage() {
  const { data: agents, isLoading } = useAgents();
  const [filter, setFilter] = useState('');
  const [hideInactive, setHideInactive] = useState(false);
  const [newDialogOpen, setNewDialogOpen] = useState(false);

  const filtered = useMemo(() => {
    if (!agents) return [];
    const q = filter.trim().toLowerCase();
    return agents.filter((a) => {
      if (hideInactive && a.status_badge === 'inactive') return false;
      if (!q) return true;
      return (
        a.name.toLowerCase().includes(q) ||
        (a.role ?? '').toLowerCase().includes(q) ||
        (a.department ?? '').toLowerCase().includes(q)
      );
    });
  }, [agents, filter, hideInactive]);

  return (
    <div className="mx-auto flex max-w-7xl flex-col gap-4">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Agents</h1>
          {agents ? (
            <p className="text-sm text-muted-foreground">{agents.length} colleagues</p>
          ) : (
            <Skeleton className="mt-1 h-4 w-28" />
          )}
        </div>
        <Button size="sm" onClick={() => setNewDialogOpen(true)}>
          <Plus className="mr-1.5 h-4 w-4" />
          New colleague
        </Button>
      </header>

      <div className="flex flex-wrap items-center justify-end gap-4">
        <div className="flex items-center gap-2">
          <input
            id="hide-inactive"
            type="checkbox"
            checked={hideInactive}
            onChange={(e) => setHideInactive(e.target.checked)}
            className="h-4 w-4 rounded border-border"
          />
          <Label htmlFor="hide-inactive" className="cursor-pointer font-normal text-muted-foreground">
            Hide inactive
          </Label>
        </div>
        <div className="relative w-64">
          <Search className="pointer-events-none absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            className="pl-8"
            placeholder="Filter by name, role, dept"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </div>
      </div>

      {isLoading ? (
        <CardGridSkeleton count={8} />
      ) : filtered.length === 0 ? (
        <div className="rounded-lg border border-dashed py-16 text-center text-muted-foreground">
          {filter ? `No agents match "${filter}".` : 'No agents to show.'}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {filtered.map((a) => (
            <AgentCard key={a.name} agent={a} />
          ))}
        </div>
      )}

      <NewColleagueDialog open={newDialogOpen} onOpenChange={setNewDialogOpen} />
    </div>
  );
}
